import { useState } from "react";
import { MessageCircle, Bug, MessageSquareQuote, ShieldCheck, Cookie } from "lucide-react";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import { useClientSession } from "@/hooks/useClientSession";
import BugReportDialog from "@/components/BugReportDialog";
import LoginDialog from "@/components/LoginDialog";
import { toast } from "sonner";
import { createPortal } from "react-dom";

interface AppFooterProps {
  fixed?: boolean;
}

const AppFooter = ({ fixed }: AppFooterProps) => {
  const { data: config } = useConfiguracoes();
  const { session } = useClientSession();
  const linkGrupoWhatsapp = config?.link_grupo_whatsapp;
  const qrcodeGrupoUrl = config?.qrcode_grupo_url;

  const [showQrCode, setShowQrCode] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const requireLogin = (action: () => void) => {
    if (session) {
      action();
    } else {
      setPendingAction(() => action);
      setLoginOpen(true);
    }
  };

  return (
    <>
      <footer className={`py-5 sm:py-4 text-center border-t border-border/30 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 ${fixed ? "fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" : "mb-4 sm:mb-0"}`}>
        <p className="font-pixel text-[7px] text-muted-foreground">⚔️ RPG Coins © 2026</p>
        {linkGrupoWhatsapp && (
          <button
            onClick={() => {
              window.open(linkGrupoWhatsapp, "_blank");
              if (qrcodeGrupoUrl) setShowQrCode(true);
            }}
            className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
            title="Grupo WhatsApp"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span className="font-pixel text-[7px]">Grupo WhatsApp</span>
          </button>
        )}
        <button
          onClick={() => requireLogin(() => setBugReportOpen(true))}
          className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
          title="Reportar Bug"
        >
          <Bug className="w-3.5 h-3.5" />
          <span className="font-pixel text-[7px]">Reportar Bug</span>
        </button>
        <a
          href="/depoimentos"
          className="flex items-center gap-1 font-pixel text-[7px] text-muted-foreground hover:text-primary transition-colors"
        >
          <MessageSquareQuote className="w-3.5 h-3.5" />
          Depoimentos
        </a>
        <a
          href="/politica-privacidade"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 font-pixel text-[7px] text-muted-foreground hover:text-primary transition-colors"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          Política de Privacidade
        </a>
        <button
          onClick={() => {
            try {
              localStorage.removeItem("lgpd-cookie-consent");
              localStorage.removeItem("lgpd-cookie-consent-ts");
            } catch { /* ignore */ }
            window.location.reload();
          }}
          className="flex items-center gap-1 font-pixel text-[7px] text-muted-foreground hover:text-primary transition-colors"
          title="Gerenciar cookies"
        >
          <Cookie className="w-3.5 h-3.5" />
          Cookies
        </button>
      </footer>

      {showQrCode && qrcodeGrupoUrl && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowQrCode(false)}>
          <div className="bg-background border border-border rounded-lg p-6 max-w-sm w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <p className="font-pixel text-[10px] text-primary uppercase">QR Code do Grupo</p>
            <img src={qrcodeGrupoUrl} alt="QR Code Grupo WhatsApp" className="w-64 h-64 object-contain rounded" />
            <div className="flex gap-2">
              <button
                className="tibia-btn rounded-sm text-[9px] px-4 py-2"
                onClick={() => {
                  navigator.clipboard.writeText(linkGrupoWhatsapp || "");
                  toast.success("Link copiado!");
                }}
              >
                Copiar Link
              </button>
              <button className="tibia-btn rounded-sm text-[9px] px-4 py-2" onClick={() => setShowQrCode(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {session && (
        <BugReportDialog
          open={bugReportOpen}
          onOpenChange={setBugReportOpen}
          reportadoPor={session.apelido || session.contato || "Anônimo"}
        />
      )}

      <LoginDialog
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onLoginSuccess={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
      />
    </>
  );
};

export default AppFooter;
